import { CompSelectionProperty, QualityTierBreakdownData } from '../../types.ts';
import { getQualityTierPrice } from './getQualityTierPrice.ts';

export type QualityTierChartPoint = {
  id: string;
  salePrice: number;
  condition: number;
  qualityTier: number;
  label: string;
  isSelected: boolean;
  salePriceLabel: string;
  conditionLabel: string;
};

export type QualityTierChartData = {
  points: QualityTierChartPoint[];
  tiers: number[];
  percentile25?: number;
  percentile75?: number;
  isLoadingConditionRatings: boolean;
};

const formatConditionLabel = (condition: number): string => `C${condition}`;

const percentile = (sorted: number[], p: number): number => {
  if (sorted.length === 1) return sorted[0]!;
  const index = (sorted.length - 1) * p;
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) return sorted[lower]!;
  const weight = index - lower;
  return sorted[lower]! + (sorted[upper]! - sorted[lower]!) * weight;
};

const flattenProperties = (data: QualityTierBreakdownData): CompSelectionProperty[] =>
  Object.keys(data)
    .map(Number)
    .sort((a, b) => a - b)
    .flatMap((tier) => data[tier]?.properties ?? []);

export const getQualityTierChartData = (
  qualityTierBreakdownData: QualityTierBreakdownData,
  selectedIds: string[],
): QualityTierChartData => {
  const selectedSet = new Set(selectedIds);
  const properties = flattenProperties(qualityTierBreakdownData);

  const selectedCompNumberById = new Map(
    selectedIds.map((id, index) => [id, index + 1] as const),
  );

  const points: QualityTierChartPoint[] = [];
  for (const property of properties) {
    const price = getQualityTierPrice(property);
    const condition = property.uadConditionRating;
    const qualityTier = property.qualityTier;
    if (!price || condition === undefined || qualityTier === undefined) continue;

    const isSelected = selectedSet.has(property.id);
    const selectedNumber = selectedCompNumberById.get(property.id);
    points.push({
      id: property.id,
      salePrice: price.toDollarLike(),
      condition,
      qualityTier,
      label:
        selectedNumber !== undefined
          ? `Comp ${selectedNumber}`
          : (property.address?.AddressLineText ?? property.id),
      isSelected,
      salePriceLabel: price.toString(true),
      conditionLabel: formatConditionLabel(condition),
    });
  }

  const tiers = [...new Set(points.map((point) => point.qualityTier))].sort((a, b) => a - b);
  const isLoadingConditionRatings = properties.some(
    (property) => property.uadRatingsLoading === true,
  );
  if (points.length < 2) {
    return { points, tiers, isLoadingConditionRatings };
  }

  const sortedPrices = points.map((point) => point.salePrice).sort((a, b) => a - b);
  return {
    points,
    tiers,
    percentile25: percentile(sortedPrices, 0.25),
    percentile75: percentile(sortedPrices, 0.75),
    isLoadingConditionRatings,
  };
};
